/** @format */

import { useContext, useEffect, useState } from 'react';
import { Box, Divider, Typography, Popper } from '@mui/material';
import { TouchApp } from '@mui/icons-material';
import { HomeContext } from '../Home';
import { TABS_MAPPING } from './KPIContainer';
import { useAppContext } from '../../../../store/store.app';
import { buildPayload } from '../../../Library/appfilter.lib';
import { getKPI } from '../../../../Services/service.home';
import styles from './kpi.module.css';

const DATA_NUMBER_SX = {
  fontFamily: '"Roboto Condensed", sans-serif',
  fontVariantNumeric: 'tabular-nums',
};

function KPICard({ kpi }) {
  const [data, setData] = useState(null);

  const { activeTab, setKpiData } = useContext(HomeContext);

  const {
    appStore: { appFilters },
  } = useAppContext();

  const { PARAMETER, CALC_DATERANGE } = appFilters;

  const payload = {
    ...buildPayload(appFilters),
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) {
      return '-';
    }

    const revRegex = /Rev$/i;
    const occRegex = /Occ/gi;

    if (occRegex.test(kpi.title)) {
      return Number(value).toFixed(1);
    }

    if (revRegex.test(kpi.title)) {
      return (Number(value) / 1000).toLocaleString('en-IN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    }

    return Number(value).toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const colorCodeRevenue = (data) => {
    const percentage = Number(data?.budget_percentage);

    if (percentage < -5) {
      return 'red';
    }

    if (percentage >= 0) {
      return 'green';
    }

    return 'orange';
  };

  useEffect(() => {
    if (!CALC_DATERANGE || !PARAMETER) {
      return;
    }

    let cancelled = false;

    const fetchKPI = async () => {
      try {
        const response = await getKPI(
          kpi.id,
          TABS_MAPPING[activeTab],
          payload
        );

        if (cancelled) {
          return;
        }

        const responseData = response?.data;

        if (!responseData) {
          return;
        }

        setData(responseData);

        setKpiData((previousData) => ({
          ...previousData,
          [kpi.id]: responseData,
        }));
      } catch (error) {
        console.error('Error fetching KPI:', kpi.id, error);
      }
    };

    fetchKPI();

    return () => {
      cancelled = true;
    };
  }, [activeTab, kpi.id, CALC_DATERANGE, PARAMETER]);

  if (!data) {
    return <div>Loading</div>;
  }

  return (
    <Box
      className={styles.card}
      sx={{
        borderLeft: {
          xs: 'none',
          sm: '1px solid silver',
        },

        borderRight: {
          xs: 'none',
          sm: 'initial',
        },

        px: {
          xs: 1,
          sm: 2,
        },

        py: {
          xs: 0.5,
          sm: 0,
        },

        flexGrow: 1,

        minWidth: {
          xs: '100%',
          sm: 220,
        },

        flexBasis: {
          xs: '100%',
          sm: 'auto',
        },

        position: 'relative',

        boxSizing: 'border-box',
      }}
    >
      <Typography
        sx={{
          fontSize: '0.825rem',
          textAlign: 'center',
        }}
      >
        {kpi.title}
      </Typography>

      <Tooltip data={data} />

      <Typography
        className={`${styles.kpiValue} data-number`}
        sx={{
          ...DATA_NUMBER_SX,
          fontSize: '1.25rem',
          textAlign: 'center',
          color: colorCodeRevenue(data),
        }}
      >
        {formatValue(data.value)}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          gap: '4px',
          justifyContent: 'space-around',
        }}
      >
        <KPIFooter
          title="Fcst"
          value={formatValue(data.forecast)}
        />

        <Divider flexItem orientation="vertical" />

        <KPIFooter
          title="Bud"
          value={formatValue(data.budget)}
        />

        <Divider flexItem orientation="vertical" />

        <KPIFooter
          title="LY"
          value={formatValue(data.last_year)}
        />
      </Box>
    </Box>
  );
}

function TooltipRow({ label, value, percentage, title }) {
  const numericPercentage = Number(percentage);

  let color = '';

  if (numericPercentage < -5) {
    color = 'red';
  } else if (numericPercentage >= 0) {
    color = 'green';
  } else {
    color = 'orange';
  }

  const displayValue =
    value === null || value === undefined
      ? '-'
      : /occ/gi.test(title)
        ? Number(value).toLocaleString('en-IN', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          })
        : Number(value).toLocaleString('en-IN', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          });

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: 1,
      }}
    >
      <Typography
        sx={{
          fontSize: '0.75rem',
        }}
      >
        {label}
      </Typography>

      <Typography
        className="data-number"
        sx={{
          ...DATA_NUMBER_SX,
          fontSize: '0.75rem',
          textAlign: 'right',
        }}
      >
        {displayValue}
      </Typography>

      <Typography
        className="data-number"
        sx={{
          ...DATA_NUMBER_SX,
          color,
          fontSize: '0.75rem',
          textAlign: 'right',
        }}
      >
        {percentage !== '' &&
        percentage !== null &&
        percentage !== undefined
          ? `${Number(percentage).toFixed(1)}%`
          : ''}
      </Typography>
    </Box>
  );
}

function Tooltip({ data }) {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMouseEnter = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMouseLeave = () => {
    setAnchorEl(null);
  };

  const handleTouch = (event) => {
    event.stopPropagation();

    setAnchorEl((previous) =>
      previous ? null : event.currentTarget
    );
  };

  return (
    <>
      <Box
        sx={{
          position: 'absolute',
          right: 2,
          top: 1,
          color: 'text.disabled',
          cursor: 'pointer',
          zIndex: 1,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleTouch}
      >
        <TouchApp
          fontSize="small"
          sx={{
            color: '#757575',
          }}
        />
      </Box>

      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        sx={{
          zIndex: 9999,
          opacity: 1,
        }}
      >
        <Box
          sx={{
            minWidth: 'fit-content',
            backgroundColor: '#e8e4e4',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 1,
            zIndex: 1300,
          }}
        >
          <TooltipRow
            label="Actual"
            title={data.title || ''}
            value={data.value}
            percentage=""
          />

          <TooltipRow
            label="Forecast"
            title={data.title || ''}
            value={data.forecast}
            percentage={data.forecast_percentage}
          />

          <TooltipRow
            label="Budget"
            title={data.title || ''}
            value={data.budget}
            percentage={data.budget_percentage}
          />

          <TooltipRow
            label="LY"
            title={data.title || ''}
            value={data.last_year}
            percentage={data.last_yr_percentage}
          />
        </Box>
      </Popper>
    </>
  );
}

function KPIFooter({ title, value }) {
  return (
    <Box>
      <Typography
        className="data-number"
        textAlign="center"
        sx={{
          ...DATA_NUMBER_SX,
          fontSize: '0.75rem',
        }}
      >
        {value}
      </Typography>

      <Typography
        textAlign="center"
        sx={{
          fontSize: '0.7rem',
        }}
      >
        {title}
      </Typography>
    </Box>
  );
}

export default KPICard;
